import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {WebsocketMessage} from '../../../shared/websocket/websocketMessage';
import {VRGroupService} from '../vrgroup.service';
import {MapElement} from '../../../shared/map/map-element.model';
import {Point} from 'geojson';
import {SidebarPanelComponent} from '../../../sidebar/sidebar-panel/sidebar-panel.component';
import {AppMessageService} from '../../../shared/app-message/app-message.service';
import {takeUntil} from 'rxjs/operators';
import {LandingPageTile} from '../../../shared/tiles/home-tile.model';
import {_} from '../../../shared/utils';
import {FilterEvent, FilterProperties, TypeFilters} from '../../../sidebar/filters/filters.model';
import {StateService} from '../../../shared/state/state.service';
import {filter, find, cloneDeep} from 'lodash-es';
import {Station} from '../common/station';
import {RunningTrain} from '../common/train';
import { AlarmMessage } from '../../../alarm/alarm-message';
import { AlarmService } from '../../../alarm/alarm.service';
import { TechnicalAlarmTypes, ConflictAlarmTypes } from '../../../alarm/alarm-definitions/interfaces';
import { InfoActionService } from '../../../element-utils/element-actions/info-action/info-action.service';
import { ITrainListComponent } from '../utils/train-list.type';
import { MapService } from '~app/shared/map/map.service';
import { default as getTenantConfig } from '~shared/map/layer/layer-definitions/T2';

/**
 * Train listing component. Lists all trains currently returned from KoneGIS and provides simple sort & filtering.
 */
@Component({
  selector: 'qm-vrgroup-train-list',
  templateUrl: './train-list.component.html',
  styleUrls: ['./train-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainListComponent extends SidebarPanelComponent implements OnInit, OnDestroy, ITrainListComponent {
  public readonly columnWidths = {
    train: '4.7em',
    status: '1.2em',
    time: '2.6em',
    route: '6.5em'
  };

  /**
   * List of displayed trains
   */
  public displayedTrains: RunningTrain[] = [];
  public alarmOverlaySelectedTrain: RunningTrain;

  public alarmOverlayTechnicalAlarm: AlarmMessage[] = [];

  /**
   * Filter displayed trains by train type
   */
  public trainTypeFilters = [
    {
      id: 1,
      name: 'Trains',
      type: 'dropdown-multiple',
      filter_values: [
        {
          name: _('Commuter'),
          value: 'COMMUTER',
          boolean: true
        },
        {
          name: _('Long-distance'),
          value: 'LONGDISTANCE',
          boolean: true
        },
        {
          name: _('Freight train'),
          value: 'FREIGHT',
          boolean: true
        },
        {
          name: _('Locomotive'),
          value: 'LOCOMOTIVE',
          boolean: false
        },
        {
          name: _('Other'),
          value: 'OTHER',
          boolean: false
        },
      ]
    }
  ];
  public typeFilter: { name: string, value: string, boolean: boolean }[] = [];
  public trainFilter: Map<string, FilterProperties[] | TypeFilters> = new Map();
  public listLimit: number = 40;

  public challengeUrl = getTenantConfig().tenantCustomData.urls.challenge;

  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();

  /**
   * @param {VRGroupService} vrGroupService
   * @param {ChangeDetectorRef} changeDetector
   * @param state
   * @param {AppMessageService} appMessageService
   */
  constructor(private vrGroupService: VRGroupService,
              private changeDetector: ChangeDetectorRef,
              private state: StateService,
              protected appMessageService: AppMessageService,
              private alarmService: AlarmService,
              private mapService: MapService,
              private infoActionService: InfoActionService) {
    super(appMessageService);
    this.componentName = TrainListComponent.getComponentName();
  }

  public static initTile(tile: LandingPageTile) {
    tile.description = _('Running trains');
    tile.icon = 'far fa-arrow-right';
    tile.title = _('Running trains');
    tile.panel = TrainListComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'TrainList';
  }

  /**
   * Angular initialize hook, subscribe to new websocket messages.
   */
  ngOnInit() {
    const temp = this.state.getPartOfState('trainList_filters');
    if (temp) {
      this.trainTypeFilters = temp;
      this.getStations();
    }
    this.saveFilterState(this.trainTypeFilters);
    for (const s of this.trainTypeFilters) {
      this.trainFilter.set(s.name, s.filter_values);
    }
    this.vrGroupService.runningTrainData$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(data => {
      this.handleNewWebsocketMessage(data);
    });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private async getStations() {
    if (this.trainTypeFilters[1] && this.trainTypeFilters[1].filter_values.length <= 0) {
      this.trainTypeFilters[1].filter_values = [];
      const stations: Station[] = await this.vrGroupService.getStationList();
      const temp: { name: string, value: string, boolean: boolean }[] = [];
      stations.forEach((o: any) => {
        temp.push({
          name: o.stationName + ' (' + o.stationShortCode + ')',
          value: o.stationShortCode,
          boolean: false
        });
      });
      this.trainTypeFilters[1].filter_values = temp;
    }
  }

  public getActiveTrainStoppedAlarms = (train: RunningTrain) => {
    return train.alarms.filter(a => a.metadata.active && a.metadata.type === 'vrTrainStopped');
  }

  public hasActiveConflictAlarms = (train: RunningTrain) => {
    return this.getActiveConflictAlarms(train).length > 0;
  }

  public hasActiveTechnicalAlarms = (train: RunningTrain) => {
    return this.getActiveTechnicalAlarms(train).length > 0;
  }

  public filterActiveAlarms(alarms: AlarmMessage[]) {
    return alarms.filter(alarm => {
      return alarm.metadata.active === true;
    });
  }

  public filterVrConflictAlarms(train: RunningTrain) {
    return train.alarms.filter(alarm => {
      return ConflictAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public filterVrTechnicalAlarms(train: RunningTrain) {
    return train.alarms.filter(alarm => {
      return TechnicalAlarmTypes.indexOf(alarm.metadata.type) !== -1;
    });
  }

  public getTranslatedAlarm = (alarm: AlarmMessage) => {
    return this.alarmService.getTranslatedAlarm(alarm.metadata, false);
  }

  public filtersChanged($event: FilterEvent) {
    if ($event) {
      for (const s of $event.customFilters) {
        this.trainFilter.set(s.name, s.filter_values);
      }
      const tempFilter = this.trainFilter;
      this.trainFilter = new Map(tempFilter);
      this.saveFilterState($event.customFilters);
      this.changeDetector.markForCheck();
    }
  }

  public selectAlarmOverlayTrain(train: RunningTrain) {
    this.alarmOverlaySelectedTrain = train;
  }

  public selectAlarmOverlayTechnicalAlarm(train: RunningTrain) {
    this.alarmOverlayTechnicalAlarm = this.filterActiveAlarms(this.filterVrTechnicalAlarms(train));
  }

  public getActiveTechnicalAlarms = (train: RunningTrain) => {
    return this.filterActiveAlarms(this.filterVrTechnicalAlarms(train));
  }

  private getActiveConflictAlarms = (train: RunningTrain) => {
    return this.filterActiveAlarms(this.filterVrConflictAlarms(train));
  }

  private saveFilterState(filters: any) {
    this.state.updateState('trainList_filters', filters);
  }

  /**
   * Handler for websocket messages. If websocket contains map data, draw data to map.
   * @param {WebsocketMessage} data
   */
  private handleNewWebsocketMessage(data: RunningTrain[]) {
    this.displayedTrains = data.sort((a, b) => {
      return +b.calculatedDelta - +a.calculatedDelta;
    }).sort((o: any) => {
      if (o.flagged) {
        return -1;
      }
      return 0;
    });
    this.saveFilterState(this.trainTypeFilters);
    if (this.displayedTrains.length > 0) {
      this.loading = false;
    }
    this.changeDetector.markForCheck();
  }

  /**
   * Return row styling for conflicts with other trains.
   * @param train - current train
   * @returns {string} -  class name for current row
   */
  public getRowClass(train: RunningTrain): string {
    return 'text-' + train.status;
  }

  /**
   * Is train visible on list?
   * @param train
   * @returns {any}
   */
  public trainVisible = (train: RunningTrain, trainFilter: Map<string, FilterProperties[] | TypeFilters>) => {
    const actives = filter(trainFilter.get('Stations'), (o: any) => {
      return o.boolean;
    });
    const type: FilterProperties[] = filter(trainFilter.get('Type'), (o: any) => {
      return o.boolean;
    });
    for (const s of trainFilter.get('Trains') as FilterProperties[]) {
      if (train && s.value === train.type) {
        if (actives.length > 0) {
          if (type.length >= 2 || type.length <= 0) {
            if (find(actives, (o: any) => {
              return o.value === train.depStation || o.value === train.arrStation;
            })) {
              return true;
            }
          } else if (type[0].value === 'arrival') {
            if (find(actives, (o: any) => {
              return o.value === train.arrStation;
            })) {
              return true;
            }
          } else {
            if (find(actives, (o: any) => {
              return o.value === train.depStation;
            })) {
              return true;
            }
          }
        } else {
          return s.boolean;
        }
      }
    }
  }

  public async openTrainInfo(train: RunningTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.mapService.changeCurrentFeature(el);
    this.infoActionService.openDetails(el.layer, el);
  }

  public async openCargoInfo(train: RunningTrain) {
    const el = await this.vrGroupService.getTrainMapElement(train.tripNumber, train.tripDate);
    this.mapService.changeCurrentFeature(el);
    this.infoActionService.openDetails(el.layer, el, 'cargomanifest');
  }

  public onLoadMoreBtnClicked() {
    this.listLimit += 30;
  }
}
